import type { SystemNotification } from './types'

interface AssignmentNotificationInput {
  recipient: string
  taskId: string
  campaignId: string
  assignedBy: string
  campaignName: string
  taskTitle?: string
  assignmentKind?: 'owner' | 'reviewer'
  createdOn?: Date
}

interface NewCampaignAssignmentNotificationInput extends AssignmentNotificationInput {
  creator: string
}

interface CampaignAlterationNotificationInput {
  recipient: string
  taskId: string
  campaignId: string
  requestedBy: string
  campaignName: string
  alterationCount: number
  createdOn?: Date
}

function notificationTimestamp(date: Date) {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  }).format(date).replace('.', '')
}

export function createAssignmentNotification({
  recipient,
  taskId,
  campaignId,
  assignedBy,
  campaignName,
  taskTitle,
  assignmentKind = 'owner',
  createdOn = new Date(),
}: AssignmentNotificationInput): SystemNotification {
  return {
    id: crypto.randomUUID(),
    recipient,
    taskId,
    campaignId,
    title: assignmentKind === 'reviewer' ? 'Nova tarefa para revisão' : taskTitle ? 'Nova tarefa atribuída' : 'Nova campanha atribuída',
    message: assignmentKind === 'reviewer' && taskTitle
      ? `${assignedBy} definiu você como revisor personalizado da tarefa “${taskTitle}” na campanha “${campaignName}”.`
      : taskTitle
      ? `${assignedBy} atribuiu a tarefa “${taskTitle}” da campanha “${campaignName}” a você.`
      : `${assignedBy} atribuiu a campanha “${campaignName}” a você.`,
    createdAt: notificationTimestamp(createdOn),
    createdOn: createdOn.toISOString(),
  }
}

export function createCampaignAlterationNotification({
  recipient,
  taskId,
  campaignId,
  requestedBy,
  campaignName,
  alterationCount,
  createdOn = new Date(),
}: CampaignAlterationNotificationInput): SystemNotification {
  return {
    id: crypto.randomUUID(),
    recipient,
    taskId,
    campaignId,
    title: 'Alterações solicitadas',
    message: `${requestedBy} reprovou a análise de “${campaignName}” e sinalizou ${alterationCount} ${alterationCount === 1 ? 'tarefa para alteração' : 'tarefas para alteração'}.`,
    createdAt: notificationTimestamp(createdOn),
    createdOn: createdOn.toISOString(),
  }
}

export function createNewCampaignAssignmentNotification({
  creator,
  ...assignment
}: NewCampaignAssignmentNotificationInput) {
  return assignment.recipient === creator ? undefined : createAssignmentNotification(assignment)
}

export function unreadTaskNotificationIds(notifications: SystemNotification[]) {
  return [...new Set(notifications.filter(notification => !notification.readAt).map(notification => notification.taskId))]
}

export function acknowledgeTaskNotifications(notifications: SystemNotification[], recipient: string, taskId: string, readAt: string) {
  return notifications.map(notification => notification.recipient === recipient && notification.taskId === taskId && !notification.readAt
    ? { ...notification, readAt }
    : notification)
}
